import { m } from '@pricedrop/i18n';
import { addMonths, format } from 'date-fns';
import { he } from 'date-fns/locale/he';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ApiError } from '@/lib/api';
import type { Notification, NotificationType } from '@/lib/notifications';
import {
  EXPIRATION_OPTIONS,
  NOTIFICATION_TYPE_OPTIONS,
  requiresPriceThreshold,
  useCreateNotification,
  useDeleteNotification,
  useUpdateNotification,
} from '@/lib/notifications';
import { ResponsiveDialog } from './responsive-dialog';

interface ItemNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemUin: number;
  itemName: string;
  currentPrice: number | null;
  existingNotification?: Notification | null;
  onSuccess?: () => void;
  onDelete?: () => void;
  scope?: 'ITEM' | 'TAGS';
  tags?: number[];
  tagNames?: string[];
  brandIds?: number[] | null;
  brandNames?: string[] | null;
}

interface NotificationFormValues {
  type: NotificationType;
  priceThreshold: number;
  expiresAt: Date | null;
}

function computeDefaults(
  currentPrice: number | null,
  existingNotification?: Notification | null,
  scope: 'ITEM' | 'TAGS' = 'ITEM',
) {
  const defaultType: NotificationType =
    existingNotification?.type ??
    (scope === 'TAGS'
      ? 'PRICE_DROP'
      : currentPrice == null || currentPrice <= 0
        ? 'BACK_IN_STOCK'
        : 'PRICE_DROP');

  let defaultThreshold: number;
  if (scope === 'TAGS') {
    defaultThreshold = existingNotification?.priceThreshold ?? 100;
  } else if (
    existingNotification?.priceThreshold != null &&
    currentPrice != null &&
    currentPrice > 0
  ) {
    defaultThreshold = Math.min(existingNotification.priceThreshold, currentPrice);
  } else if (currentPrice != null && currentPrice > 0) {
    defaultThreshold = Math.floor(currentPrice * 0.8);
  } else {
    defaultThreshold = 100;
  }

  const defaultExpiresAt: Date = existingNotification?.expiresAt
    ? new Date(existingNotification.expiresAt as string)
    : addMonths(new Date(), 3);

  return { defaultType, defaultThreshold, defaultExpiresAt };
}

export function ItemNotificationDialog({
  open,
  onOpenChange,
  itemUin,
  itemName,
  currentPrice,
  existingNotification,
  onSuccess,
  onDelete,
  scope = 'ITEM',
  tags,
  tagNames,
  brandIds,
  brandNames,
}: ItemNotificationDialogProps) {
  const isEditing = !!existingNotification;
  const { defaultType, defaultThreshold, defaultExpiresAt } = React.useMemo(
    () => computeDefaults(currentPrice, existingNotification, scope),
    [currentPrice, existingNotification, scope],
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<NotificationFormValues>({
    defaultValues: {
      type: defaultType,
      priceThreshold: defaultThreshold,
      expiresAt: defaultExpiresAt,
    },
  });

  const createMutation = useCreateNotification();
  const updateMutation = useUpdateNotification();
  const deleteMutation = useDeleteNotification();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  const isLoading =
    isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const isInStock = currentPrice != null && currentPrice > 0;
  const watchedType = watch('type');
  const showSlider = requiresPriceThreshold(watchedType) && (scope === 'TAGS' || isInStock);

  const availableTypeOptions = React.useMemo(
    () =>
      NOTIFICATION_TYPE_OPTIONS.filter((option) => {
        if (option.value === 'BACK_IN_STOCK') return !isInStock && scope !== 'TAGS';
        if (
          option.value === 'PRICE_BELOW_THRESHOLD' ||
          option.value === 'LOWEST_PRICE' ||
          option.value === 'PRICE_DROP'
        )
          return scope === 'TAGS' || isInStock;
        return true; // ANY_CHANGE always available
      }),
    [isInStock, scope],
  );

  React.useEffect(() => {
    if (open) {
      reset({
        type: defaultType,
        priceThreshold: defaultThreshold,
        expiresAt: defaultExpiresAt,
      });
      setShowDeleteConfirmation(false);
    }
  }, [open, defaultType, defaultThreshold, defaultExpiresAt, reset]);

  async function onSubmit(values: NotificationFormValues) {
    try {
      if (isEditing && existingNotification) {
        await updateMutation.mutateAsync({
          id: existingNotification.id,
          type: values.type,
          priceThreshold: requiresPriceThreshold(values.type) ? values.priceThreshold : undefined,
          expiresAt: values.expiresAt?.toISOString(),
        });
        toast.success(m.notifications_toast_updated());
      } else {
        scope === 'TAGS'
          ? await createMutation.mutateAsync({
              scope: 'TAGS',
              type: values.type,
              tags: tags ?? [],
              brandIds: brandIds && brandIds.length > 0 ? brandIds : undefined,
              priceThreshold: requiresPriceThreshold(values.type)
                ? values.priceThreshold
                : undefined,
              expiresAt: values.expiresAt?.toISOString(),
            })
          : await createMutation.mutateAsync({
              scope: 'ITEM',
              type: values.type,
              itemUin,
              priceThreshold: requiresPriceThreshold(values.type)
                ? values.priceThreshold
                : undefined,
              expiresAt: values.expiresAt?.toISOString(),
            });
        toast.success(m.notifications_toast_created());
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CONFLICT') {
        toast.info(m.notifications_toast_duplicate());
        return;
      }
      toast.error(m.notifications_toast_error());
    }
  }

  async function handleDelete() {
    if (!existingNotification) return;
    try {
      await deleteMutation.mutateAsync({
        id: existingNotification.id,
        itemUin: existingNotification.itemUin,
        tags: existingNotification.tags,
        brandIds: existingNotification.brandIds ?? undefined,
      });
      toast.success(m.notifications_toast_deleted());
      onOpenChange(false);
      onDelete?.();
    } catch {
      toast.error(m.notifications_toast_error());
    }
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={m.notifications_dialog_title()}
      description={
        scope === 'TAGS'
          ? brandNames && brandNames.length > 0
            ? m.notifications_tags_brand_dialog_description()
            : m.notifications_tags_dialog_description()
          : m.notifications_dialog_description()
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {scope === 'TAGS' ? (
          <div className="rounded-lg border border-input bg-muted/30 p-3 space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              {m.notifications_tags_reference_label()}
            </p>
            <div className="flex flex-wrap gap-2">
              {(tagNames ?? []).map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  {name}
                </span>
              ))}
            </div>
            {brandNames && brandNames.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {brandNames.map((name) => (
                  <span
                    key={name}
                    role="img"
                    aria-label={m.notifications_brand_pill_label()}
                    className="inline-flex items-center rounded-full border border-purple-200 bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800 dark:border-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {brandNames && brandNames.length > 0
                ? m.notifications_tags_brand_reference_hint()
                : m.notifications_tags_reference_hint()}
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-muted/50 p-3">
            <p className="font-medium text-sm">{itemName}</p>
            <p className="text-muted-foreground text-xs">
              {m.notifications_item_number()}
              {itemUin}
            </p>
            {currentPrice != null && currentPrice > 0 && (
              <p className="mt-1 font-semibold text-sm">
                {m.notifications_threshold_current()}: ₪{currentPrice.toLocaleString()}
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label>{m.notifications_type_label()}</Label>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {showSlider && (
          <div className="space-y-3">
            <Label>
              {scope === 'TAGS'
                ? m.notifications_tags_threshold_label()
                : m.notifications_threshold_label()}
            </Label>
            {scope === 'TAGS' ? (
              <Controller
                control={control}
                name="priceThreshold"
                render={({ field }) => (
                  <div className="space-y-2">
                    <Input
                      type="number"
                      min={1}
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="w-full text-center text-lg font-bold"
                    />
                  </div>
                )}
              />
            ) : currentPrice != null ? (
              <Controller
                control={control}
                name="priceThreshold"
                render={({ field }) => (
                  <div className="space-y-3">
                    <p className="text-center text-2xl font-bold">
                      ₪{field.value.toLocaleString()}
                    </p>
                    <Slider
                      min={1}
                      max={currentPrice}
                      step={1}
                      value={[field.value]}
                      onValueChange={(vals) => field.onChange(vals[0])}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>₪1</span>
                      <span>₪{currentPrice.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              />
            ) : null}
            <p className="text-xs text-muted-foreground">
              {scope === 'TAGS'
                ? m.notifications_tags_threshold_hint()
                : m.notifications_threshold_hint()}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <Label>{m.notifications_expiration_label()}</Label>
          <div className="flex w-full items-center gap-2 rounded-lg border border-input bg-background p-3 text-sm">
            <span>
              {watch('expiresAt') ? format(watch('expiresAt') as Date, 'PPP', { locale: he }) : ''}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {EXPIRATION_OPTIONS.map((option) => {
              const targetDate = addMonths(new Date(), option.months);
              const currentExpiresAt = watch('expiresAt');
              const isActive =
                currentExpiresAt &&
                Math.abs(currentExpiresAt.getTime() - targetDate.getTime()) < 24 * 60 * 60 * 1000;
              return (
                <Button
                  key={option.months}
                  type="button"
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className="h-10"
                  onClick={() => setValue('expiresAt', targetDate)}
                >
                  {option.label()}
                </Button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">{m.notifications_expiration_hint()}</p>
        </div>

        {showDeleteConfirmation ? (
          <div className="space-y-4 rounded-lg border-2 border-destructive/20 bg-destructive/10 p-4">
            <div className="text-center">
              <p className="mb-2 font-semibold text-destructive">
                {m.notifications_delete_confirm_title()}
              </p>
              <p className="text-sm text-muted-foreground">
                {m.notifications_delete_confirm_description()}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeleteConfirmation(false)}
                disabled={isLoading}
                className="flex-1"
              >
                {m.notifications_delete_confirm_cancel()}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isLoading}
                className="flex-1"
              >
                {deleteMutation.isPending
                  ? m.notifications_delete_deleting()
                  : m.notifications_delete_confirm_delete()}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg py-3 font-medium"
            >
              {isSubmitting
                ? m.notifications_submit_saving()
                : isEditing
                  ? m.notifications_submit_update()
                  : m.notifications_submit_create()}
            </Button>
            {isEditing && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeleteConfirmation(true)}
                disabled={isLoading}
                className="w-full rounded-lg border-destructive py-3 font-medium text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                {m.notifications_delete_button()}
              </Button>
            )}
          </div>
        )}
      </form>
    </ResponsiveDialog>
  );
}
